import React, { useEffect, useState } from 'react';
import type { MenuItem, DishSnapshot } from '../../../shared/types';
import DishPicker from './DishPicker';

interface AmountAndDishesFormProps {
    initialAmount?: number; // centimes
    initialDishes?: DishSnapshot[];
    menu: MenuItem[];
    onChange: (state: { amountCents: number | null; dishes: DishSnapshot[] }) => void;
}

const eurosToCents = (euros: string): number | null => {
    if (!euros || euros.trim() === '') return null;
    const parsed = parseFloat(euros.replace(',', '.'));
    if (isNaN(parsed) || parsed < 0) return null;
    return Math.round(parsed * 100);
};

const centsToEuros = (cents: number | undefined): string =>
    typeof cents === 'number' && cents > 0 ? (cents / 100).toFixed(2) : '';

const AmountAndDishesForm: React.FC<AmountAndDishesFormProps> = ({
    initialAmount,
    initialDishes,
    menu,
    onChange,
}) => {
    const [amountInput, setAmountInput] = useState<string>(centsToEuros(initialAmount));
    const [dishes, setDishes] = useState<DishSnapshot[]>(initialDishes ?? []);
    const [pickerMode, setPickerMode] = useState<'catalog' | 'free'>(menu.length > 0 ? 'catalog' : 'free');
    const [freeText, setFreeText] = useState({ name: '', quantity: 1, unitPrice: '' });

    // Notifier le parent à chaque changement
    useEffect(() => {
        onChange({ amountCents: eurosToCents(amountInput), dishes });
    }, [amountInput, dishes, onChange]);

    const addCatalogItem = (item: MenuItem) => {
        setDishes(prev => {
            const existing = prev.find(d => d.name === item.name);
            if (existing) {
                return prev.map(d => d.name === item.name ? { ...d, quantity: d.quantity + 1 } : d);
            }
            return [
                ...prev,
                {
                    menuItemId: item.id,
                    name: item.name,
                    quantity: 1,
                    unitPrice: Math.round(item.price * 100), // € → centimes
                },
            ];
        });
    };

    const removeCatalogItem = (itemName: string) => {
        setDishes(prev => {
            const existing = prev.find(d => d.name === itemName);
            if (!existing) return prev;
            if (existing.quantity <= 1) return prev.filter(d => d.name !== itemName);
            return prev.map(d => d.name === itemName ? { ...d, quantity: d.quantity - 1 } : d);
        });
    };

    const addFreeItem = () => {
        const name = freeText.name.trim();
        const price = parseFloat(freeText.unitPrice.replace(',', '.'));
        if (!name || isNaN(price) || price < 0) return;
        setDishes(prev => [
            ...prev,
            { name, quantity: freeText.quantity, unitPrice: Math.round(price * 100) },
        ]);
        setFreeText({ name: '', quantity: 1, unitPrice: '' });
    };

    const removeDishAt = (index: number) => {
        setDishes(prev => prev.filter((_, i) => i !== index));
    };

    return (
        <div className="space-y-5">
            {/* Montant */}
            <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">
                    Montant payé (€) <span className="text-gray-400 font-normal">— optionnel</span>
                </label>
                <input
                    type="number"
                    inputMode="decimal"
                    step="0.01"
                    min="0"
                    placeholder="0,00"
                    value={amountInput}
                    onChange={e => setAmountInput(e.target.value)}
                    className="w-full p-2.5 bg-gray-50 border border-gray-400 rounded-lg outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-500"
                />
            </div>

            {/* Plats */}
            <div>
                <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-bold text-gray-700">
                        Plats consommés <span className="text-gray-400 font-normal">— optionnel</span>
                    </label>
                    {menu.length > 0 && (
                        <div className="flex gap-1 text-xs">
                            <button
                                type="button"
                                onClick={() => setPickerMode('catalog')}
                                className={`px-2 py-1 rounded font-medium ${pickerMode === 'catalog' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600'}`}
                            >
                                Carte
                            </button>
                            <button
                                type="button"
                                onClick={() => setPickerMode('free')}
                                className={`px-2 py-1 rounded font-medium ${pickerMode === 'free' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600'}`}
                            >
                                Libre
                            </button>
                        </div>
                    )}
                </div>

                {pickerMode === 'catalog' && menu.length > 0 ? (
                    <DishPicker
                        menu={menu}
                        currentItems={dishes}
                        onAdd={addCatalogItem}
                        onRemove={removeCatalogItem}
                    />
                ) : (
                    <div className="space-y-2">
                        <div className="flex gap-2">
                            <input
                                type="text"
                                placeholder="Nom du plat"
                                value={freeText.name}
                                onChange={e => setFreeText(f => ({ ...f, name: e.target.value }))}
                                className="flex-1 p-2 text-sm bg-gray-50 border border-gray-400 rounded outline-none focus:ring-1 focus:ring-brand-500 focus:border-indigo-400"
                            />
                            <input
                                type="number"
                                min="1"
                                step="1"
                                value={freeText.quantity}
                                onChange={e => setFreeText(f => ({ ...f, quantity: parseInt(e.target.value) || 1 }))}
                                className="w-16 p-2 text-sm bg-gray-50 border border-gray-400 rounded outline-none focus:ring-1 focus:ring-brand-500 focus:border-indigo-400"
                            />
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                placeholder="€"
                                value={freeText.unitPrice}
                                onChange={e => setFreeText(f => ({ ...f, unitPrice: e.target.value }))}
                                className="w-20 p-2 text-sm bg-gray-50 border border-gray-400 rounded outline-none focus:ring-1 focus:ring-brand-500 focus:border-indigo-400"
                            />
                            <button
                                type="button"
                                onClick={addFreeItem}
                                className="px-3 py-2 bg-indigo-600 text-white rounded text-sm font-semibold hover:bg-indigo-700"
                            >
                                +
                            </button>
                        </div>
                    </div>
                )}

                {/* Récap des plats sélectionnés */}
                {dishes.length > 0 && (
                    <ul className="mt-3 space-y-1">
                        {dishes.map((d, i) => (
                            <li key={`${d.name}-${i}`} className="flex items-center justify-between text-xs bg-gray-50 px-2 py-1.5 rounded">
                                <span className="text-gray-700">
                                    <span className="font-semibold">{d.quantity}×</span> {d.name}
                                    <span className="text-gray-400"> · {(d.unitPrice / 100).toFixed(2)}€</span>
                                </span>
                                <button
                                    type="button"
                                    onClick={() => removeDishAt(i)}
                                    className="text-red-500 hover:text-red-700 font-bold ml-2"
                                    aria-label={`Supprimer ${d.name}`}
                                >
                                    ×
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
};

export default AmountAndDishesForm;
